import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ApiErrorModal } from '@/components/ApiErrorModal';
import type { ApiErrorReason } from '@/consts/events';

interface ApiErrorOptions {
  titleOverride?: string;
  subtitleOverride?: string;
  reason?: ApiErrorReason;
  stepName?: string;
}

interface ApiErrorContextValue {
  showApiError: (options?: ApiErrorOptions) => void;
  hideApiError: () => void;
}

const ApiErrorContext = createContext<ApiErrorContextValue | undefined>(undefined);

const ApiErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ApiErrorOptions>({});

  const showApiError = useCallback((errorOptions?: ApiErrorOptions) => {
    setOptions(errorOptions || {});
    setIsOpen(true);
  }, []);

  const hideApiError = useCallback(() => {
    setIsOpen(false);
    setOptions({});
  }, []);

  const value = useMemo(
    () => ({
      showApiError,
      hideApiError,
    }),
    [showApiError, hideApiError]
  );

  return (
    <ApiErrorContext.Provider value={value}>
      {children}
      <ApiErrorModal
        isOpen={isOpen}
        onClose={hideApiError}
        titleOverride={options.titleOverride}
        subtitleOverride={options.subtitleOverride}
        reason={options.reason}
        stepName={options.stepName}
      />
    </ApiErrorContext.Provider>
  );
};

const useApiError = () => {
  const context = useContext(ApiErrorContext);
  if (!context) {
    throw new Error('useApiError must be used within an ApiErrorProvider');
  }
  return context;
};

export { ApiErrorProvider, useApiError };
